/**
 * SOUND A/B レイヤーの生成とアレンジメントの組み立て。
 *
 * - SOUND A: メインメロディへのユニゾン（オクターブ下）/ 下ハモ
 * - SOUND B: ツインモード。メインと同じリズムを 3 度下で追走する
 *   エコーカノン（輪唱）。OFFSET はメイン最後の音（= SOUND A の最後の音）を
 *   0.0.0 とした符号付きで、負は前倒し（重なり）、正は後ろへずらす。
 *   休符を織り交ぜてスパースにする
 *
 * すべて純関数。タイミング情報のみを生成する。
 */

import {
  buildScalePool,
  getScaleById,
  shiftScaleDegrees,
} from "./musicTheory";
import {
  PPQ,
  TICKS_PER_BAR,
  generatePhrase,
  mulberry32,
  type PhraseNote,
  type PhraseParams,
} from "./phraseGenerator";

export type SoundAMode = "off" | "unison" | "harmony";

/**
 * SOUND B のオフセット。メイン最後の音（= SOUND A の最後の音）を 0.0.0 とし、
 * SOUND B の先頭ノートをそこから何 bar.beat ずらすかを符号付きで表す。
 */
export interface TwinOffsetDef {
  id: string;
  label: string;
  /** 符号付き tick（負 = 前倒し / 正 = 後ろへ） */
  ticks: number;
}

const BEATS_PER_BAR = TICKS_PER_BAR / PPQ;

/** OFFSET の可動幅（拍）。中央 0.0.0 から前後 ±2 小節。 */
const TWIN_OFFSET_MAX_BEATS = 2 * BEATS_PER_BAR;

/** 符号付き拍数 → `±bar.beat.0` 表記のオフセット定義 */
const twinOffset = (beats: number): TwinOffsetDef => {
  const abs = Math.abs(beats);
  const sign = beats < 0 ? "-" : beats > 0 ? "+" : "";
  const label = `${sign}${Math.floor(abs / BEATS_PER_BAR)}.${abs % BEATS_PER_BAR}.0`;
  return { id: label, label, ticks: beats * PPQ };
};

/** 中央が 0.0.0、左（負）で前倒し、右（正）で後ろへ。1 拍刻みで並べる。 */
export const TWIN_OFFSETS: TwinOffsetDef[] = Array.from(
  { length: TWIN_OFFSET_MAX_BEATS * 2 + 1 },
  (_, i) => twinOffset(i - TWIN_OFFSET_MAX_BEATS),
);

/** 中央（0.0.0）のインデックス。ノブの初期位置・フォールバックに使う。 */
export const TWIN_ZERO_OFFSET_INDEX = TWIN_OFFSET_MAX_BEATS;

/** ツインの休符抽選に使う派生シード（メインの生成とは独立した乱数列を保つ） */
const TWIN_SEED_OFFSET = 7777;

export interface LayerParams {
  soundAMode: SoundAMode;
  soundBEnabled: boolean;
  /** TWIN_OFFSETS の id */
  soundBOffsetId: string;
}

export const DEFAULT_LAYER_PARAMS: LayerParams = {
  soundAMode: "off",
  soundBEnabled: false,
  soundBOffsetId: "0.0.0",
};

export interface Arrangement {
  main: PhraseNote[];
  /** SOUND A: ユニゾン/ハモリ（OFF 時は空） */
  soundA: PhraseNote[];
  /** SOUND B: ツイン = エコーカノン（OFF 時は空） */
  soundB: PhraseNote[];
  /** メインフレーズの終点 tick */
  endTick: number;
  /** 全レイヤーを含む長さ（最後のノートを含む小節境界まで切り上げた tick） */
  lengthTicks: number;
}

/** 重ねるレイヤーのベロシティ倍率（メインより一段引っ込める） */
const LAYER_VELOCITY_SCALE = 0.85;

/** 下ハモの度数（スケール上の 2 度数下 = ダイアトニック 3 度下） */
const HARMONY_DEGREES = -2;

/** ツインで各ノートを休符に置き換える確率（終止音は対象外） */
const TWIN_REST_PROBABILITY = 0.25;

const clampVelocity = (v: number): number =>
  Math.min(127, Math.max(1, Math.round(v)));

/** ハモリ用の写像（スケールプール上で HARMONY_DEGREES ぶん移動） */
const harmonyShiftFor = (params: PhraseParams): ((midi: number) => number) => {
  const pool = buildScalePool(params.rootPc, getScaleById(params.scaleId));
  return (midi) => shiftScaleDegrees(pool, midi, HARMONY_DEGREES);
};

/**
 * メインノートから派生レイヤーのノートを作る。
 * ピッチ（glide 先も含む）を写像し、ベロシティを一段下げ、tick をずらす。
 */
const deriveNote = (
  src: PhraseNote,
  shiftPitch: (midi: number) => number,
  tickOffset = 0,
): PhraseNote => ({
  ...src,
  tick: src.tick + tickOffset,
  midiNote: shiftPitch(src.midiNote),
  velocity: clampVelocity(src.velocity * LAYER_VELOCITY_SCALE),
  ...(src.glideToMidi !== undefined
    ? { glideToMidi: shiftPitch(src.glideToMidi) }
    : {}),
});

/** SOUND A: メインノートをオクターブ下 or スケール 3 度下（2 度数下）へ写像 */
const buildSoundA = (
  main: PhraseNote[],
  mode: SoundAMode,
  params: PhraseParams,
): PhraseNote[] => {
  if (mode === "off") return [];

  const shift =
    mode === "unison"
      ? (midi: number) => Math.max(0, midi - 12)
      : harmonyShiftFor(params);

  return main.map((n) => deriveNote(n, shift));
};

/**
 * SOUND B（ツイン）: メインと並走する「エコーカノン（輪唱）」。
 *
 * メインメロディそのものをスケール上の 3 度下（2 度数下）へ移し、
 * 先頭ノートが「メイン最後の音 + OFFSET」に来るようずらして追走させる。
 * リズム・音価・加速感はメインと完全に同一なので、スピード感が必ず揃う。
 * 負の OFFSET はメインに重なるずれたハモリとして響き、0.0.0 は最後の音と
 * 同時に始まる応答、正の OFFSET はさらに間を置いた残響のような応答になる。
 *
 * 約 1/4 のノートは確率的に休符へ置き換え（終止音は必ず残す）、ただの
 * 複製にならないスパースさを保つ。シードはメインから派生させ、決定論を
 * 保つ（OFFSET を変えても休符の位置は変わらず、平行移動になる）。
 */
const buildSoundB = (
  params: PhraseParams,
  main: PhraseNote[],
  offsetId: string,
): PhraseNote[] => {
  if (main.length === 0) return [];

  const offset =
    TWIN_OFFSETS.find((o) => o.id === offsetId) ??
    TWIN_OFFSETS[TWIN_ZERO_OFFSET_INDEX];
  // メイン最後の音を基準に、OFFSET ぶんずらした位置へ全ノートを平行移動する
  const mainLastTick = main[main.length - 1].tick;
  const tickShift = mainLastTick + offset.ticks;
  const rand = mulberry32(params.seed + TWIN_SEED_OFFSET);
  const shiftPitch = harmonyShiftFor(params);

  const notes: PhraseNote[] = [];
  for (let i = 0; i < main.length; i++) {
    const isLast = i === main.length - 1;
    // 休符抽選は OFFSET に依らず全ノートぶん引き切る（乱数列を固定して平行移動を保つ）
    const rest = rand() < TWIN_REST_PROBABILITY;
    if (rest && !isLast) continue;

    const derived = deriveNote(main[i], shiftPitch, tickShift);
    if (derived.tick < 0) continue; // 前倒しでフレーズ先頭より前へ出たぶんは捨てる
    notes.push(derived);
  }
  return notes;
};

/**
 * アレンジメント生成のエントリポイント。
 * 再生・WAV・MIDI・ピアノロールはすべてこの結果を共有する。
 */
export const generateArrangement = (
  params: PhraseParams,
  layers: LayerParams,
): Arrangement => {
  const phrase = generatePhrase(params);

  const main = phrase.notes;
  const endTick = phrase.endTick;

  const soundA = buildSoundA(main, layers.soundAMode, params);
  const soundB = layers.soundBEnabled
    ? buildSoundB(params, main, layers.soundBOffsetId)
    : [];

  const lastTick = Math.max(endTick, ...soundB.map((n) => n.tick));
  const lengthTicks = Math.ceil((lastTick + 1) / TICKS_PER_BAR) * TICKS_PER_BAR;

  return { main, soundA, soundB, endTick, lengthTicks };
};

/** レイヤー識別子（再生・書き出しで音色を切り替えるために使う） */
export type LayerId = "main" | "soundA" | "soundB";

export interface ArrangementEvent {
  layer: LayerId;
  note: PhraseNote;
}

/** 全レイヤーを tick 順のフラットなイベント列にする（スケジューラ用） */
export const flattenArrangement = (arr: Arrangement): ArrangementEvent[] => {
  const events: ArrangementEvent[] = [];
  const push = (layer: LayerId, notes: PhraseNote[]) => {
    for (const note of notes) events.push({ layer, note });
  };
  push("main", arr.main);
  push("soundA", arr.soundA);
  push("soundB", arr.soundB);
  events.sort((a, b) => a.note.tick - b.note.tick);
  return events;
};
